import React, { useState, useEffect } from 'react';
import { connect } from 'react-redux';
import Label from '../ui/Label/FilterLabel';
import { Button, InlineUploaderLayout, RadioButton } from '../ui';
import { IconEyeView } from '../ui/Icons';
import { ALLOWED_FILE_TYPES, ATTACHMENT_MAX_SIZES } from '../../consts/consts';
import { uploadFile } from '../../redux/fileUpload/slice';
import { NOTIFICATION_MESSAGES } from '../../localization/Notifications/dictionary';
import renderNotification from '../../helpers/notifications/renderNotification';
import DropzoneArea from './DropzoneArea';
import FileList from './FileList';

const {
  UploaderWrapper,
  UploaderHeader,
  Description,
  Title,
  FlexWrapper,
  ButtonWrapper,
  ContentWrapper,
  ContentRightWrapper
} = InlineUploaderLayout;
const { Group: RadioGroup } = RadioButton;

const InlineUploaderComponent = ({
  uploadFile,
  onDeleteFiles,
  fileContainerId = '',
  disabled = false,
  viewOnly,
  iconOnlyButtons = false,
  value,
  onChange = () => {},
  onSelectionChange = () => {},
  schema = {
    allowedFileTypes: ALLOWED_FILE_TYPES.default,
    title: 'Datei hinzufügen',
    description: '',
    maxFiles: 1
  },
  hideDescriptionAfterUpload,
  activeFileId,
  requireDeleteConfirmation = true,
  showFilesCount = true,
  status
}) => {
  const [selectedOption, setSelectedOption] = useState(value ? 'custom' : 'default');
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (value && selectedOption !== 'custom') {
      setSelectedOption('custom');
    } else if (!value && selectedOption !== 'default') {
      setSelectedOption('default');
    }
  }, [value]);

  const { description, title, maxFiles, allowedFileTypes, defaultViewUrl } = schema;

  const filesArray = value ? [value] : [];

  const allowedMimeTypesSource =
    (allowedFileTypes.accept ? Object.keys(allowedFileTypes.accept) : null) ||
    allowedFileTypes.list ||
    allowedFileTypes.mimeTypes ||
    allowedFileTypes;

  const allowedMimeTypes = Array.isArray(allowedMimeTypesSource) ? allowedMimeTypesSource : [];

  const handleDropRejected = (rejectedFiles) => {
    if (!rejectedFiles || rejectedFiles.length === 0) return;

    const fileName = rejectedFiles[0]?.file?.name || '';
    const errorCodes = rejectedFiles.flatMap((f) => (f.errors || []).map((e) => e.code));

    let message;

    if (errorCodes.includes('too-many-files')) {
      message = `Es können maximal ${maxFiles} Datei${maxFiles === 1 ? '' : 'en'} hochgeladen werden.`;
    } else if (errorCodes.includes('file-too-large')) {
      message = 'Datei ist zu groß.';
    } else {
      message = NOTIFICATION_MESSAGES.message_attachment_invalid_file_type.replace('$FILE_NAME', fileName);
    }

    renderNotification({ type: 'error', message });
  };

  const handleFileDrop = (acceptedFiles) => {
    if (acceptedFiles.length === 0) return;

    const file = acceptedFiles[0];

    if (allowedMimeTypes.length > 0 && !allowedMimeTypes.includes(file.type)) {
      renderNotification({
        type: 'error',
        message: NOTIFICATION_MESSAGES.message_attachment_invalid_file_type.replace('$FILE_NAME', file.name)
      });
      return;
    }

    if (file.size > ATTACHMENT_MAX_SIZES.default.byte) {
      renderNotification({ type: 'error', message: 'Datei ist zu groß.' });
      return;
    }
    setIsLoading(true);

    uploadFile({
      file: file,
      fileContainerId: fileContainerId,
      callback: (response) => {
        setIsLoading(false);
        if (response && response.id) {
          onChange(response);
          setSelectedOption('custom');
          onSelectionChange('custom');
        } else {
          renderNotification({ type: 'error', message: 'Upload fehlgeschlagen.' });
          onChange(null);
          setSelectedOption('default');
          onSelectionChange('default');
        }
      }
    });
  };

  const handleOnDeleteFile = (fileId) => {
    onDeleteFiles(fileId);
    onChange(null);
    onSelectionChange('default');
  };

  const handleRadioChange = (e) => {
    const newOption = e.target.value;
    setSelectedOption(newOption);
    onSelectionChange(newOption);
    if (newOption === 'default') {
      onChange(null);
    }
  };

  const handleViewTemplate = () => {
    if (defaultViewUrl) {
      window.open(defaultViewUrl, '_blank');
    } else {
      alert('View Template');
    }
  };

  const amountOfFiles = filesArray.length;
  const formattedLabel = `${amountOfFiles} / ${maxFiles}`;
  const viewTemplateTitle = 'Mustertext ansehen';

  const isCustomSelectedAndNoValue = selectedOption === 'custom' && !value;
  const showDropzoneContainer = isCustomSelectedAndNoValue || selectedOption === 'default';
  const showFileList = value && selectedOption === 'custom';
  const isDropzoneDisabled = selectedOption === 'default';
  const hasError = status === 'error' && isCustomSelectedAndNoValue;

  const renderedDescription = () => {
    if (hideDescriptionAfterUpload && filesArray.length > 0) {
      return null;
    }
    return <Description>{description}</Description>;
  };

  return (
    <UploaderWrapper>
      <UploaderHeader>
        <FlexWrapper>
          <Title>{title}</Title>
          {showFilesCount && <Label>{formattedLabel}</Label>}
        </FlexWrapper>
      </UploaderHeader>
      {renderedDescription()}

      <ContentWrapper>
        <RadioGroup value={selectedOption} onChange={handleRadioChange} className="vertical">
          <RadioButton value="default">oqio Mustertexte</RadioButton>
          <RadioButton value="custom">Eigene Datei</RadioButton>
        </RadioGroup>

        <ContentRightWrapper>
          <ButtonWrapper>
            <Button onClick={handleViewTemplate} type="link" icon={<IconEyeView />}>
              {viewTemplateTitle}
            </Button>
          </ButtonWrapper>

          <div>
            {showFileList && (
              <FileList
                files={[value]}
                onDeleteFile={handleOnDeleteFile}
                disabled={disabled}
                viewOnly={viewOnly}
                iconOnlyButtons={iconOnlyButtons}
                activeFileId={activeFileId}
                requireDeleteConfirmation={requireDeleteConfirmation}
              />
            )}

            {showDropzoneContainer && !showFileList && (
              <DropzoneArea
                onDrop={handleFileDrop}
                onDropRejected={handleDropRejected}
                allowedFileTypes={allowedFileTypes}
                constraintText={allowedFileTypes.description}
                isLoading={isLoading}
                maxNumberOfFilesMessage={maxFiles}
                disabled={isDropzoneDisabled || disabled}
                hasError={hasError}
              />
            )}
          </div>
        </ContentRightWrapper>
      </ContentWrapper>
    </UploaderWrapper>
  );
};

InlineUploaderComponent.displayName = 'InlineUploader';

const mapStateToProps = () => ({});

const mapDispatchToProps = {
  uploadFile
};

export const InlineUploader = connect(mapStateToProps, mapDispatchToProps)(InlineUploaderComponent);
